import React, { useState } from "react";

import { useAppSelector, useAppDispatch } from "../../app/hooks";

import { Link as RLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import SouthIcon from "@mui/icons-material/South";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";

import { useShiftPlannedStartsMutation } from "../../api/ticket";
import { getRelativeShortDisplay } from "../../util/datetime";
import { TicketItem, selectByIds, displayKType } from "../ticket/ticketSlice";
import { getTestData, Data, getRowsData } from "./timeTableSlice";

const testRows = getRowsData();

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: number | string | boolean | null },
    b: { [key in Key]: number | string | boolean | null },
) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
    width: number;
}

const headCells: readonly HeadCell[] = [
    {
        id: "start",
        numeric: false,
        disablePadding: true,
        label: "Start",
        width: 50,
    },
    {
        id: "end",
        numeric: false,
        disablePadding: true,
        label: "End",
        width: 80,
    },
    {
        id: "title",
        numeric: false,
        disablePadding: true,
        label: "Title",
        width: 400,
    },
    {
        id: "parent",
        numeric: false,
        disablePadding: false,
        label: "prnt",
        width: 30,
    },
    //*
    {
        id: "context",
        numeric: false,
        disablePadding: false,
        label: "Ctxt",
        width: 30,
    },
    // */
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: /*string*/ keyof Data,
    ) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
    } = props;
    const createSortHandler =
        (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={
                            numSelected > 0 && numSelected < rowCount
                        }
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            "aria-label": "select all desserts",
                        }}
                    />
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ width: headCell.width }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc"
                                        ? "sorted descending"
                                        : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface EnhancedTableToolbarProps {
    selected: string[];
    numSelected: number;
}

function constructShifts(items: { [key: string]: TicketItem }, shift: number) {
    let res = [];
    for (const key in items) {
        let ps = items[key].time.planned_start;
        let d = ps ? new Date(ps) : new Date();
        d.setTime(d.getTime() + shift);
        res.push({
            id: items[key].metadata.id,
            planned_start: d.toISOString(),
        });
    }
    return { shifts: res };
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
    const { selected, numSelected } = props;
    const [shiftPlannedStarts, result] = useShiftPlannedStartsMutation();
    const sps = useAppSelector(selectByIds(selected));
    return (
        <Toolbar
            sx={[
                {
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                },
                numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(
                            theme.palette.primary.main,
                            theme.palette.action.activatedOpacity,
                        ),
                },
            ]}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: "1 1 100%" }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: "1 1 100%" }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Tickets
                </Typography>
            )}
            {numSelected > 0 ? (
                <Tooltip title="Yesterday">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, -24 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <UndoIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="-8h">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, -8 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <UndoIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="+1h">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, 60 * 60 * 1000),
                            );
                        }}
                    >
                        <SouthIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="+6h">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, 6 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <SouthIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="Tomorrow">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, 24 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <RedoIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="After Tomorrow">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, 2 * 24 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <RedoIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="Next Week">
                    <IconButton
                        onClick={() => {
                            return shiftPlannedStarts(
                                constructShifts(sps, 7 * 24 * 60 * 60 * 1000),
                            );
                        }}
                    >
                        <RedoIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box />
            )}
            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Filter list">
                    <IconButton>
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
}

function areSortedDataListsEquals(dl1: Data[], dl2: Data[]) {
    //console.log("202411090633 are sorted equals", dl1, dl2);
    if (dl1.length !== dl2.length) {
        return false;
    }
    //dl1.forEach((el: Data, idx: number) => {
    for (let idx = 0; idx < dl1.length; idx++) {
        let el = dl1[idx];
        //console.log("202411090633 ti equals", dl2[idx], el);
        if (el.id === "10844") {
            console.log("10844", dl2[idx].start, el.start);
        }
        if (dl2[idx].id !== el.id) {
            return false;
        }
        if (dl2[idx].start === null && el.start !== null) {
            return false;
        }
        if (dl2[idx].start !== null && el.start === null) {
            return false;
        }
        let dlstart = dl2[idx].start ? dl2[idx].start : "";
        if (
            dl2[idx].start !== null &&
            el.start !== null &&
            (dl2[idx].start || "").localeCompare(el.start) !== 0
        ) {
            console.log("202411090633 finally returning false !!");
            return false;
        }
    }
    //console.log("202411090635 are sorted equals is returning true ..");
    return true;
}

interface ChildProps {
    data: Data[];
    refDate: Date;
    titleLink: (id: string) => string;
    linkTo: "parent" | "context" | undefined;
}

export default function EnhancedTable(props: ChildProps) {
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<keyof Data>("start");
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(true);
    const [rowsPerPage, setRowsPerPage] = React.useState(40);

    //console.log("202411081715 - time table - props data", props.data);
    //const rows = (props.data) ? props.data : testRows;
    const [rows, setRows] = React.useState(props.data);
    if (!areSortedDataListsEquals(rows, props.data)) {
        //console.log("202412160732: setting rows - - - - - - - -");
        setRows(props.data);
        setSelected([]);
    }

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSelectAllClick = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDense(event.target.checked);
    };

    const isSelected = (id: string) => selected.indexOf(id) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const visibleRows = [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    /*
  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage],
  );
  // */
    return (
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
                <EnhancedTableToolbar
                    selected={Object.assign([], selected)}
                    numSelected={selected.length}
                />
                <TableContainer>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size={dense ? "small" : "medium"}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {visibleRows /*rows*/
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.id);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    const refDate = props.refDate;
                                    const startStr = row.start
                                        ? getRelativeShortDisplay(
                                              props.refDate,
                                              new Date(row.start),
                                          )
                                        : "";
                                    const endStr = row.end
                                        ? getRelativeShortDisplay(
                                              props.refDate,
                                              new Date(row.end),
                                          )
                                        : ">";

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) =>
                                                handleClick(event, row.id)
                                            }
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.id}
                                            selected={isItemSelected}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isItemSelected}
                                                    inputProps={{
                                                        "aria-labelledby":
                                                            labelId,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                <Tooltip title={row.start}>
                                                    <Typography variant="body2">
                                                        {startStr}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography variant="body2">
                                                    {endStr}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography
                                                    variant="body2"
                                                    title={row.title}
                                                    sx={{
                                                        overflow: "hidden",
                                                        textWrap: "nowrap",
                                                        textOverflow:
                                                            "ellipsis",
                                                        width: 400,
                                                    }}
                                                >
                                                    <Link
                                                        component={RLink}
                                                        to={props.titleLink(
                                                            row.id,
                                                        )}
                                                    >
                                                        {row.title}
                                                    </Link>{" "}
                                                    ({displayKType(row.ktype)})
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Link
                                                    component={RLink}
                                                    to={
                                                        "/ticket/view/" +
                                                        row.parent
                                                    }
                                                >
                                                    {row.parent}
                                                </Link>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Link
                                                    component={RLink}
                                                    to={
                                                        "/ticket/byContext/" +
                                                        row.context
                                                    }
                                                >
                                                    {row.context}
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (dense ? 33 : 53) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 40]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            <FormControlLabel
                control={
                    <Switch checked={dense} onChange={handleChangeDense} />
                }
                label="Dense padding"
            />
        </Box>
    );
}
